import React, { useState, useEffect } from 'react';
import EmployeeModal from './EmployeeModal';
import ResetPasswordModal from './ResetPasswordModal';
import DataTable from 'react-data-table-component';
import { authorizedRequest } from '../../api/authorizedRequest';
import authService from '../../services/AuthService';

interface Employee {
  id: string;
  name: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
  isActive: boolean;
  userId: string;
}

interface Role {
  id: string;
  name: string;
}

const ManageEmployee: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedEmployeeName, setSelectedEmployeeName] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [filterId, setFilterId] = useState<string>('');
  const [filterName, setFilterName] = useState<string>('');
  const [filterRoles, setFilterRoles] = useState<string[]>([]);
  const [inputId, setInputId] = useState<string>('');
  const [inputName, setInputName] = useState<string>('');
  const [inputRoles, setInputRoles] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [sortColumn, setSortColumn] = useState<string>('Id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterOpen, setFilterOpen] = useState(false);

// Fetch employees whenever paging, sorting, or filtering changes
useEffect(() => {
  fetchEmployees();
}, [currentPage, rowsPerPage, sortColumn, sortDirection, filterId, filterName, filterRoles]);

// Fetch roles only once on mount
useEffect(() => {
  fetchRoles();
}, []);


  const handleApplyFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setFilterId(inputId);
    setFilterName(inputName);
    setFilterRoles(inputRoles);
    setCurrentPage(1); // Reset to first page on filter
  };

  const handleClearFilter = () => {
    setInputId('');
    setInputName('');
    setInputRoles([]);
    setFilterId('');
    setFilterName('');
    setFilterRoles([]);
    setSortColumn('Id');
    setCurrentPage(1);
  };
  
  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skipCount', ((currentPage - 1) * rowsPerPage).toString());
      params.append('maxResultCount', rowsPerPage.toString());
      params.append('sorting', `${sortColumn} ${sortDirection}`);

      if (filterId) {
        params.append('id', filterId);
      }
      if (filterName) {
        params.append('name', filterName);
      }
      if (filterRoles && filterRoles.length > 0) {
        filterRoles.forEach(role => params.append('roles', role));
      }

      const response = await authorizedRequest({
        url: '/Employee',
        method: 'GET',
        params,
      });
      setEmployees(response.data.items || []);
      setTotalRows(response.data.totalCount || 0);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await authorizedRequest({ url: '/Role', method: 'GET' });
      setRoles(response.data || []);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleView = (employee: Employee) => {
    setSelectedEmployeeId(employee.id);
    setShowModal(true);
  };
  const handleModalClose = () => {
    setShowModal(false);
    setSelectedEmployeeId(null);
  };

  const handleModalSave = (data: any) => {
    // Implement save logic here (e.g., call API to update employee)
    setShowModal(false);
    setSelectedEmployeeId(null);
  };

  const handleResetPassword = (employee: Employee) => {
    setSelectedUserId(employee.userId);
    setSelectedEmployeeName(employee.name);
    setShowResetPasswordModal(true);
  };

  const handleResetPasswordModalClose = () => {
    setShowResetPasswordModal(false);
    setSelectedUserId(null);
    setSelectedEmployeeName(null);
  };

  const handleEdit = (employee: Employee) => {
    // Implement edit logic here
    alert(`Edit employee: ${employee.name}`);
  };

  const handleDelete = (employee: Employee) => {
    // Implement delete logic here
    if (window.confirm(`Are you sure you want to delete ${employee.name}?`)) {
      alert(`Deleted employee: ${employee.name}`);
    }
  };

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#343a40', // dark background
        color: 'white',
      },
    },
    headCells: {
      style: {
        color: 'white', // light foreground for text and sort icons
        fontWeight: 'bold',
      },
    },
  };

  const columns = [
    {
      name: 'Id',
      selector: (row: Employee) => row.id,
      sortable: true,
       width: "5%",
       sortField: "Id",
    },
    {
      name: 'Name',
      selector: (row: Employee) => row.name,
      sortable: true,
      width: "25%",
      sortField: "Name",
    },
    {
      name: 'Address 1',
      selector: (row: Employee) => row.address1,
      sortable: true,
      width: "30%",
      sortField: "Address1",
    },
    {
      name: 'City',
      selector: (row: Employee) => row.city,
      sortable: true,
      width: "15%",
      sortField: "City",
    },
    {
      name: 'State',
      selector: (row: Employee) => row.state,
      sortable: true,
      width: "5%",
      sortField: "State",
    },
    {
      name: 'Zip',
      selector: (row: Employee) => row.zip,
      sortable: true,
      width: "5%",
      sortField: "Zip",
    },
    {
      name: 'Is Active',
      selector: (row: Employee) => (row.isActive ? 'Yes' : 'No'),
      sortable: true,
      sortField: "IsActive",
      width: "5%",
    },
    {
      name: 'Actions',
      cell: (row: Employee) => (
        <div className="dropdown">
          <button
            className="btn btn-secondary btn-sm dropdown-toggle"
            type="button"
            id={`dropdownMenuButton-${row.id}`}
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            Actions
          </button>
          <div className="dropdown-menu" aria-labelledby={`dropdownMenuButton-${row.id}`}>
            <button className="dropdown-item" onClick={() => handleView(row)}>View</button>
            {authService.hasRequiredRole(['Admin']) && (
              <button className="dropdown-item" onClick={() => handleResetPassword(row)}>Reset Password</button>
            )}
            {/* <button className="dropdown-item" onClick={() => handleEdit(row)}>Edit</button> */}
            <button className="dropdown-item text-danger" onClick={() => handleDelete(row)}>Delete</button>
          </div>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "10%",
    },
  ];

  return (
  <div className="pd-ltr-20">
        <div className="card-box pd-20 height-100-p mb-30">
          <div className="row align-items-center">
            <div className="col-md-12">
              <h4 className="font-20 weight-500 mb-10 text-capitalize">
                Manage Employees
              </h4>
            </div>
          </div>
        </div>
        <div className="accordion mb-3" id="filterAccordion">
          <div className="card">
            <div className="card-header p-0" id="headingFilter">
              <h2 className="mb-0">
                <button
                  className="btn btn-link btn-block text-left"
                  type="button"
                  onClick={() => setFilterOpen(!filterOpen)}
                  aria-expanded={filterOpen}
                  aria-controls="collapseFilter"
                >
                  Filter
                </button>
              </h2>
            </div>
            <div
              id="collapseFilter"
              className={`collapse${filterOpen ? ' show' : ''}`}
              aria-labelledby="headingFilter"
              data-parent="#filterAccordion"
            >
              <div className="card-body">
                <form onSubmit={handleApplyFilter} autoComplete="off">
                  <div className="row">
                    <div className="col-md-4 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Filter by ID"
                        value={inputId}
                        onChange={e => setInputId(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Filter by Name"
                        value={inputName}
                        onChange={e => setInputName(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4 mb-2">
                      <select multiple className="form-control" onChange={(e) => setInputRoles(Array.from(e.target.selectedOptions, option => option.value))}>
                        {roles.map(role => (
                          <option key={role.id} value={role.id}>{role.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12 text-right">
                      <button type="submit" className="btn btn-secondary mt-2 mr-2">
                        Apply Filter
                      </button>
                      <button type="button" className="btn btn-outline-secondary mt-2" onClick={handleClearFilter}>
                        Clear Filter
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className="card-box mb-30">
          <div className='min-height-200px'>
            <div className="pd-20">
              <DataTable
                title="Employee List"
                columns={columns}
                customStyles={customStyles}
                data={employees}
                progressPending={loading} 
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={rowsPerPage}
                paginationDefaultPage={currentPage}
                onChangeRowsPerPage={(newPerPage) => setRowsPerPage(newPerPage)}
                onChangePage={(page) => setCurrentPage(page)}
                highlightOnHover
                pointerOnHover
                sortServer
                onSort={(column, sortDirection) => {
                 setSortColumn((column.sortField || column.name?.toString()) ?? "Id");
                  setSortDirection(sortDirection);
                }}
              />
            </div>
          </div>
        </div>
         <EmployeeModal
        show={showModal}
        onClose={handleModalClose}
        employeeId={selectedEmployeeId}
        onSave={handleModalSave}
      />
      <ResetPasswordModal
        show={showResetPasswordModal}
        onClose={handleResetPasswordModalClose}
        userId={selectedUserId}
        employeeName={selectedEmployeeName}
      />
      </div>
  );
};

export default ManageEmployee;